import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AuthContext } from '../context/AuthContext';
import usePageMeta from '../hooks/usePageMeta';
import useUsernameCheck from '../hooks/useUsernameCheck';
import CharacterCustomizer from '../components/profile/CharacterCustomizer';

/** Mirrors MAX_LENGTH in server/utils/username.js. */
const USERNAME_MAX = 16;

/**
 * Account settings.
 *
 * Every method used here already existed on AuthContext and had no callers —
 * the server endpoints and the context plumbing were built and then never given
 * a screen. This is the screen.
 *
 * Three independent forms rather than one: they hit different endpoints, fail
 * differently, and deleting your account should never be one stray Enter away
 * from renaming yourself.
 */

const card = 'bg-white dark:bg-gray-800 rounded-lg shadow-md p-6';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const inputClass = 'block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm';
const buttonClass = 'mt-4 flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-navy bg-primary-light hover:bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-primary-light/50 transition-colors';

/** Green for done, red for failed. Cleared whenever a form is resubmitted. */
function Notice({ error, success }) {
  if (!error && !success) return null;
  return (
    <div className={`rounded-md p-3 mb-4 ${error ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
      <p className={`text-sm ${error ? 'text-red-800 dark:text-red-400' : 'text-green-800 dark:text-green-400'}`}>
        {error || success}
      </p>
    </div>
  );
}

Notice.propTypes = {
  error: PropTypes.string,
  success: PropTypes.string,
};

Notice.defaultProps = {
  error: '',
  success: '',
};

function Profile() {
  usePageMeta({ title: 'Profile', description: 'Your account settings.', canonicalPath: '/profile' });

  const {
    user, updateProfile, changePassword, deleteAccount,
  } = useContext(AuthContext);
  const navigate = useNavigate();

  // --- Identity ---
  const [username, setUsername] = useState(user?.username || '');
  const [fullName, setFullName] = useState(user?.fullName || '');
  const [appearance, setAppearance] = useState(user?.appearance || {});
  const [idState, setIdState] = useState({ error: '', success: '', loading: false });
  // The current name is passed so re-typing it reads as "idle" rather than
  // "taken" — your own row holds it.
  const nameCheck = useUsernameCheck(username, user?.username);

  // --- Password ---
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwState, setPwState] = useState({ error: '', success: '', loading: false });

  // --- Danger zone ---
  const [confirmEmail, setConfirmEmail] = useState('');
  const [delState, setDelState] = useState({ error: '', loading: false });

  const saveIdentity = async (e) => {
    e.preventDefault();
    setIdState({ error: '', success: '', loading: true });
    try {
      await updateProfile({ username: username.trim(), fullName, appearance });
      setIdState({
        error: '',
        // Say the quiet part: the nameplate is latched for the life of a game
        // session, so a rename made while a /play tab is open will not appear
        // above your head until that tab reconnects.
        success: 'Saved. Your nameplate updates next time you enter the game.',
        loading: false,
      });
    } catch (err) {
      setIdState({ error: err.message, success: '', loading: false });
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwState({ error: '', success: '', loading: true });
    try {
      await changePassword(currentPassword, newPassword, confirmPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwState({ error: '', success: 'Password updated.', loading: false });
    } catch (err) {
      setPwState({ error: err.message, success: '', loading: false });
    }
  };

  const removeAccount = async (e) => {
    e.preventDefault();
    setDelState({ error: '', loading: true });
    try {
      await deleteAccount(confirmEmail);
      // deleteAccount clears the token, so this is already a logged-out user.
      navigate('/');
    } catch (err) {
      setDelState({ error: err.message, loading: false });
    }
  };

  if (!user) return null;

  const nameUnavailable = nameCheck.status === 'unavailable';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Profile</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Signed in as
            {' '}
            {user.email}
          </p>
        </div>

        {/* --- Identity --- */}
        <form onSubmit={saveIdentity} className={card}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Identity</h2>
          <Notice error={idState.error} success={idState.success} />

          <div className="space-y-4">
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="username" className={labelClass}>Username</label>
              <input
                id="username"
                type="text"
                maxLength={USERNAME_MAX}
                required
                className={inputClass}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <p className="mt-1 text-xs">
                {nameCheck.status === 'checking' && <span className="text-gray-500 dark:text-gray-400">Checking…</span>}
                {nameCheck.status === 'available' && <span className="text-green-600 dark:text-green-400">Available.</span>}
                {nameUnavailable && <span className="text-red-600 dark:text-red-400">{nameCheck.reason}</span>}
                {nameCheck.status === 'idle' && (
                  <span className="text-gray-500 dark:text-gray-400">
                    This appears above your character and in chat.
                  </span>
                )}
              </p>
            </div>

            <div>
              <span className={labelClass}>Character</span>
              <CharacterCustomizer appearance={appearance} onChange={setAppearance} />
            </div>

            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="full-name" className={labelClass}>
                Full name
                <span className="text-gray-400 dark:text-gray-500 font-normal"> (optional, private)</span>
              </label>
              <input
                id="full-name"
                type="text"
                className={inputClass}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
          </div>

          <button type="submit" className={buttonClass} disabled={idState.loading || nameUnavailable}>
            {idState.loading ? 'Saving…' : 'Save'}
          </button>
        </form>

        {/* --- Password --- */}
        <form onSubmit={savePassword} className={card}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Password</h2>
          <Notice error={pwState.error} success={pwState.success} />

          <div className="space-y-4">
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="current-password" className={labelClass}>Current password</label>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                required
                className={inputClass}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="new-password" className={labelClass}>New password</label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                className={inputClass}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="confirm-password" className={labelClass}>Confirm new password</label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                className={inputClass}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <button type="submit" className={buttonClass} disabled={pwState.loading}>
            {pwState.loading ? 'Updating…' : 'Change password'}
          </button>
        </form>

        {/* --- Danger zone --- */}
        <form onSubmit={removeAccount} className={`${card} border border-red-200 dark:border-red-900/40`}>
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">Delete account</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            This cannot be undone. Type your email address to confirm.
          </p>
          <Notice error={delState.error} success="" />

          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="confirm-email" className={labelClass}>Email address</label>
          <input
            id="confirm-email"
            type="email"
            className={inputClass}
            placeholder={user.email}
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
          />

          <button
            type="submit"
            /* Disabled until the email matches exactly — the server checks this
               too, but a destructive button should not look armed when it is
               going to be refused. */
            disabled={delState.loading || confirmEmail !== user.email}
            className="mt-4 flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-600/40 disabled:cursor-not-allowed transition-colors"
          >
            {delState.loading ? 'Deleting…' : 'Delete my account'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Profile;
